import { useState } from 'react';
import * as Blockly from 'blockly';
import { isWebSerialSupported } from '@spblock/core';
import { useStore } from '../state/store';
import { requireWorkspace } from '../lib/workspace';
import { flashToBoard } from '../flash/workflow';
import { saveProject, CloudError } from '../lib/cloud';
import { markCloudDirty } from '../lib/autosave';
import { AccountMenu } from './AccountMenu';
import {
  IconTrash,
  IconSave,
  IconCode,
  IconBot,
  IconGear,
  IconFlash,
  IconCheck,
  IconAlert,
  IconClose,
} from './icons';

export function Header() {
  const projectName = useStore((s) => s.projectName);
  const aiOpen = useStore((s) => s.aiOpen);
  const toggleAi = useStore((s) => s.toggleAi);
  const setSettingsOpen = useStore((s) => s.setSettingsOpen);
  const setCodeModalOpen = useStore((s) => s.setCodeModalOpen);
  const setView = useStore((s) => s.setView);
  const flashPhase = useStore((s) => s.flashPhase);
  const flashProgress = useStore((s) => s.flashProgress);
  const resetForNewProject = useStore((s) => s.resetForNewProject);
  const pushLine = useStore((s) => s.pushLine);
  const user = useStore((s) => s.auth.user);
  const saveStatus = useStore((s) => s.saveStatus);
  const lastSavedAt = useStore((s) => s.lastSavedAt);
  const setProjectName = useStore((s) => s.setProjectName);

  const [confirmNew, setConfirmNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const serialSupported = isWebSerialSupported();
  const busy = flashPhase === 'generating' || flashPhase === 'compiling' || flashPhase === 'flashing';

  function onNewProject(): void {
    setConfirmNew(false);
    const ws = requireWorkspace();
    ws.clear();
    localStorage.removeItem('spblock.workspace');
    resetForNewProject();
    pushLine('info', 'New project. The canvas is empty.');
  }

  async function onSave(): Promise<void> {
    setSaving(true);
    try {
      const ws = requireWorkspace();
      const data = JSON.stringify(Blockly.serialization.workspaces.save(ws));
      const { project } = await saveProject(projectName || 'untitled', data);
      pushLine('ok', `Saved "${project.name}" to the cloud (${Math.max(1, Math.round(project.sizeBytes / 1024))} KB).`);
    } catch (err) {
      const message = err instanceof CloudError ? err.message : 'Could not save to the cloud.';
      pushLine('err', message);
    } finally {
      setSaving(false);
    }
  }

  function onRenameProject(name: string): void {
    setProjectName(name);
    markCloudDirty();
  }

  const saveChip = (() => {
    switch (saveStatus) {
      case 'dirty':
        return <span className="save-chip dirty" title="Changes will auto-save to the cloud a few seconds after you stop editing">Unsaved changes</span>;
      case 'saving':
        return <span className="save-chip saving">Auto-saving…</span>;
      case 'saved':
        return (
          <span className="save-chip saved" title={lastSavedAt ? new Date(lastSavedAt).toLocaleTimeString() : undefined}>
            Auto-saved{lastSavedAt ? ` ${new Date(lastSavedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
          </span>
        );
      case 'error':
        return <span className="save-chip error" title="Auto-save failed — check your connection or press Save">Save failed</span>;
      default:
        return null;
    }
  })();

  const flashLabel = (() => {
    switch (flashPhase) {
      case 'generating':
        return 'Generating…';
      case 'compiling':
        return 'Compiling…';
      case 'flashing':
        return flashProgress !== null ? `Flashing ${Math.round(flashProgress)}%` : 'Flashing…';
      case 'monitoring':
        return 'Monitor running';
      case 'error':
        return 'Retry flash';
      default:
        return 'Flash to Board';
    }
  })();

  return (
    <header className="header app-header">
      <button className="wordmark" onClick={() => setView('home')} title="Back to your projects">
        SPblock
      </button>

      <input
        className="project-name"
        value={projectName}
        aria-label="Project name"
        onChange={(e) => onRenameProject(e.target.value)}
        spellCheck={false}
      />

      <div className="header-actions">
        <button className="btn btn-icon btn-danger" title="Clear the canvas and start over" onClick={() => setConfirmNew(true)}>
          <IconTrash />
        </button>
        {saveChip}
        <button className="btn" onClick={() => void onSave()} disabled={saving} title="Save the project to your cloud storage">
          <IconSave />
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button className="btn" onClick={() => setCodeModalOpen(true)} title="Inspect the generated Arduino C++">
          <IconCode />
          C++
        </button>

        <button
          className={`btn btn-toggle ${aiOpen ? 'is-on' : ''}`}
          onClick={toggleAi}
          title="Toggle the AI assistant panel"
        >
          <IconBot />
          AI Assistant
        </button>
        <button className="btn btn-icon" onClick={() => setSettingsOpen(true)} title="Settings">
          <IconGear />
        </button>

        <button
          className={`btn btn-primary ${busy ? 'btn-busy' : ''}`}
          onClick={() => void flashToBoard()}
          disabled={!serialSupported || busy}
          title={
            serialSupported
              ? 'Generate, compile, and flash the program to a connected ESP32'
              : 'Web Serial is unavailable in this browser — use Chrome or Edge 90+'
          }
        >
          {busy ? <span className="flash-progress-ring" /> : flashPhase === 'monitoring' ? <IconCheck /> : flashPhase === 'error' ? <IconAlert /> : <IconFlash />}
          {flashLabel}
        </button>

        {user && <AccountMenu />}
      </div>

      {confirmNew && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Confirm new project">
          <div className="modal" style={{ width: 380 }}>
            <div className="modal-head">
              <div className="modal-title">Clear the canvas?</div>
              <button className="modal-x" aria-label="Close" onClick={() => setConfirmNew(false)}>
                <IconClose width={14} height={14} />
              </button>
            </div>
            <div className="modal-body" style={{ fontSize: 12.5, color: 'var(--c-text-2)', lineHeight: 1.6 }}>
              This removes all blocks on the canvas. Save or download the project first if you
              want to keep it.
            </div>
            <div className="modal-foot">
              <button className="btn" onClick={() => setConfirmNew(false)}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={onNewProject}>
                <IconTrash />
                Clear canvas
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
